// Service pour gérer les tableaux depuis l'API avec gestion d'erreurs améliorée
import axios from 'axios';
import ErrorHandler from './errorHandler.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001');
const API_PREFIX = `${API_BASE_URL}/api`;

// Détection du mode développement
const IS_DEV_MODE = process.env.NODE_ENV === 'development';

// Données statiques de db.json pour le mode dev
const DB_JSON_DATA = {
  tableaux: [
    {
      id: 1,
      titre: "Soleil levant",
      description: "Un paysage au lever du soleil",
      image: "uploads/img_brabus_noel_1753639191913_761187066.png",
      prix: "1500€",
      disponibilite: "disponible",
      categories: [],
      technique: "Huile sur toile",
      dateCreation: "2023-01-15"
    },
    {
      id: 2,
      titre: "Mer calme",
      description: "Une mer paisible au crépuscule",
      image: "img_brabus_noel_1753639191913_761187066.png",
      prix: "1200€",
      disponibilite: "indisponible",
      categories: [],
      technique: "Acrylique",
      dateCreation: "2022-08-02"
    },
    {
      id: 3,
      titre: "Forêt enchantée",
      description: "Un chemin mystérieux à travers la forêt",
      image: "img_brabus_noel_1753639191913_761187066.png",
      prix: "1800€",
      disponibilite: "disponible",
      categories: [],
      technique: "Aquarelle",
      dateCreation: "2021-11-21"
    }
  ],
  categories: [
    { id: 1, nom: "Paysages", description: "Tableaux de paysages" },
    { id: 2, nom: "Abstraits", description: "Art abstrait" },
    { id: 3, nom: "Portraits", description: "Portraits et figures" }
  ]
};

function normalizeImageUrl(img) {
  if (!img) return img;
  // Déjà absolue
  if (/^https?:\/\//i.test(img)) return img;
  // Cas /uploads/xxx
  if (img.startsWith('/uploads/')) {
    return `${API_BASE_URL.replace(/\/$/, '')}${img}`;
  }
  // Cas uploads/xxx sans slash initial
  if (img.startsWith('uploads/')) {
    return `${API_BASE_URL.replace(/\/$/, '')}/${img}`;
  }
  return img;
}

// Configuration axios
axios.defaults.timeout = 15000;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    return Promise.reject(error);
  }
);

// --- API Tableaux de base ---
export const getTableaux = async (params = {}) => {
  if (IS_DEV_MODE) {
    console.log('🔧 Mode DEV: Chargement des tableaux depuis db.json');
    const all = DB_JSON_DATA.tableaux;
    return { data: all, total: all.length };
  }

  try {
    const response = await axios.get(`${API_PREFIX}/tableaux`, { params });
    const raw = response.data;
    const list = Array.isArray(raw) ? raw : (raw.data || []);
    const total = Array.isArray(raw) ? raw.length : (raw.total ?? list.length);
    return { data: list.map((t) => ({ ...t, image: normalizeImageUrl(t.image) })), total };
  } catch (error) {
    ErrorHandler.logApiError(error, 'Get Tableaux', { endpoint: `${API_PREFIX}/tableaux` });
    const fallback = DB_JSON_DATA.tableaux;
    return { data: fallback, total: fallback.length };
  }
};

export const getTableauById = async (id) => {
  try {
    if (IS_DEV_MODE) console.log(`📡 Récupération du tableau ID: ${id}`);
    const response = await axios.get(`${API_PREFIX}/tableaux/${id}`);
    const data = response.data ? { ...response.data, image: normalizeImageUrl(response.data.image) } : null;
    if (IS_DEV_MODE) console.log('✅ Tableau récupéré:', data?.titre || 'Sans titre');
    return data;
  } catch (error) {
    ErrorHandler.logApiError(error, `Get Tableau By ID ${id}`, {
      endpoint: `${API_PREFIX}/tableaux/${id}`,
      tableauId: id,
      operation: 'fetch_by_id',
    });

    if (IS_DEV_MODE) console.error(`❌ Erreur lors de la récupération du tableau ${id}:`, error.message);
    return null;
  }
};

export const searchTableaux = async (searchParams = {}) => {
  try {
    if (IS_DEV_MODE) console.log('🔍 Recherche de tableaux avec params:', searchParams);
    const response = await axios.get(`${API_PREFIX}/tableaux/search`, {
      params: searchParams,
    });
    if (IS_DEV_MODE) console.log('✅ Résultats de recherche:', response.data?.length || 0);
    return response.data || [];
  } catch (error) {
    ErrorHandler.logApiError(error, 'Search Tableaux', {
      endpoint: `${API_PREFIX}/tableaux/search`,
      searchParams,
      operation: 'search',
    });
    if (IS_DEV_MODE) console.error('❌ Erreur lors de la recherche de tableaux:', error.message);
    return [];
  }
};

export const createTableau = async (tableauData) => {
  try {
    if (IS_DEV_MODE) console.log("➕ Création d'un nouveau tableau:", tableauData?.titre);
    const response = await axios.post(`${API_PREFIX}/tableaux`, tableauData);
    if (IS_DEV_MODE) console.log('✅ Tableau créé avec succès, ID:', response.data?.id);
    return response.data;
  } catch (error) {
    ErrorHandler.logApiError(error, 'Create Tableau', {
      endpoint: `${API_PREFIX}/tableaux`,
      tableauData: { titre: tableauData?.titre, artiste: tableauData?.artiste },
      operation: 'create',
    });
    if (IS_DEV_MODE) console.error('❌ Erreur lors de la création du tableau:', error.message);
    throw error;
  }
};

export const updateTableau = async (idOrData, maybeData) => {
  try {
    const data = typeof idOrData === 'object' ? idOrData : maybeData;
    const id = typeof idOrData === 'object' ? idOrData.id : idOrData;
    if (IS_DEV_MODE) console.log(`🔄 Mise à jour du tableau ID: ${id}`);
    const response = await axios.put(`${API_PREFIX}/tableaux/${id}`, data);
    if (IS_DEV_MODE) console.log('✅ Tableau mis à jour avec succès');
    return response.data;
  } catch (error) {
    const id = typeof idOrData === 'object' ? idOrData.id : idOrData;
    const data = typeof idOrData === 'object' ? idOrData : maybeData;
    ErrorHandler.logApiError(error, `Update Tableau ${id}`, {
      endpoint: `${API_PREFIX}/tableaux/${id}`,
      tableauId: id,
      tableauData: { titre: data?.titre, artiste: data?.artiste },
      operation: 'update',
    });
    if (IS_DEV_MODE) console.error(`❌ Erreur lors de la mise à jour du tableau ${id}:`, error.message);
    throw error;
  }
};

export const deleteTableau = async (id) => {
  try {
    if (IS_DEV_MODE) console.log(`🗑️ Suppression du tableau ID: ${id}`);
    await axios.delete(`${API_PREFIX}/tableaux/${id}`);
    if (IS_DEV_MODE) console.log('✅ Tableau supprimé avec succès');
    return { success: true };
  } catch (error) {
    ErrorHandler.logApiError(error, `Delete Tableau ${id}`, {
      endpoint: `${API_PREFIX}/tableaux/${id}`,
      tableauId: id,
      operation: 'delete',
    });
    if (IS_DEV_MODE) console.error(`❌ Erreur lors de la suppression du tableau ${id}:`, error.message);
    throw error;
  }
};

// Export alias pour compatibilité avec AdminAddForm
export const addTableau = createTableau;

// Gestion du cache et de la synchronisation
async function updateLocalCache() {
  try {
    const tableaux = await getTableaux();
    localStorage.setItem('tableaux_cache', JSON.stringify(tableaux));
    localStorage.setItem('tableaux_cache_timestamp', Date.now().toString());
  } catch (error) {
    console.debug('Impossible de mettre à jour le cache:', error.message);
  }
}

function addToPendingQueue(action, data) {
  const pending = JSON.parse(localStorage.getItem('tableaux_pending') || '[]');
  pending.push({ action, data, timestamp: Date.now() });
  localStorage.setItem('tableaux_pending', JSON.stringify(pending));
}

// Synchronisation des données en attente
export async function syncPendingData() {
  const pending = JSON.parse(localStorage.getItem('tableaux_pending') || '[]');
  if (pending.length === 0) return;

  if (process.env.NODE_ENV === 'development') {
    console.log(`🔄 Synchronisation de ${pending.length} opérations en attente...`);
  }

  for (const operation of pending) {
    try {
      switch (operation.action) {
        case 'add':
          await axios.post(`${API_PREFIX}/tableaux`, operation.data);
          break;
        case 'update':
          await axios.put(`${API_PREFIX}/tableaux/${operation.data.id}`, operation.data);
          break;
        case 'delete':
          await axios.delete(`${API_PREFIX}/tableaux/${operation.data.id}`);
          break;
      }
    } catch (error) {
      console.warn('Échec de synchronisation pour une opération:', error.message);
    }
  }

  localStorage.removeItem('tableaux_pending');
  await updateLocalCache();
  if (process.env.NODE_ENV === 'development') {
    console.log('✅ Synchronisation terminée');
  }
}

// Fonctions pour la gestion des disponibilités
export async function updateDisponibilite(id, disponibilite) {
  try {
    const res = await axios.put(`${API_PREFIX}/tableaux/${id}`, { disponibilite });
    updateLocalCache();
    return res.data;
  } catch (error) {
    if (error.message === 'API_OFFLINE') {
      addToPendingQueue('update', { id, disponibilite });
      return { id, disponibilite };
    }
    throw error;
  }
}

// Fonctions pour les catégories - utiliser l'API
export async function getCategories() {
  try {
    const res = await axios.get(`${API_PREFIX}/categories`);
    return res.data;
  } catch (error) {
    if (error.message === 'API_OFFLINE') {
      const cached = localStorage.getItem('categories_cache');
      return cached ? JSON.parse(cached) : [];
    }
    return [];
  }
}

export async function addCategory(nom, description = '') {
  const newCategory = {
    nom,
    description,
    dateCreation: new Date().toISOString(),
  };

  try {
    const res = await axios.post(`${API_PREFIX}/categories`, newCategory);
    return { success: true, category: res.data };
  } catch (error) {
    if (error.message === 'API_OFFLINE') {
      addToPendingQueue('addCategory', newCategory);
      return { success: true, category: { ...newCategory, id: Date.now(), pending: true } };
    }
    if (error.response?.status === 409) {
      return { success: false, message: 'Cette catégorie existe déjà' };
    }
    throw error;
  }
}

export async function deleteCategory(id) {
  try {
    await axios.delete(`${API_PREFIX}/categories/${id}`);
    return { success: true };
  } catch (error) {
    if (error.message === 'API_OFFLINE') {
      addToPendingQueue('deleteCategory', { id });
      return { success: true };
    }
    throw error;
  }
}

export async function updateCategory(id, nom, description = '') {
  const updatedCategory = { nom, description };

  try {
    const res = await axios.put(`${API_PREFIX}/categories/${id}`, updatedCategory);
    return { success: true, category: res.data };
  } catch (error) {
    if (error.message === 'API_OFFLINE') {
      addToPendingQueue('updateCategory', { id, ...updatedCategory });
      return { success: true, category: { id, ...updatedCategory } };
    }
    if (error.response?.status === 409) {
      return { success: false, message: 'Une autre catégorie porte déjà ce nom' };
    }
    throw error;
  }
}

// --- Favoris: implémentation locale synchrone ---
const FAVORIS_KEY = 'favoris_cache';

export function getFavoris() {
  try {
    const raw = localStorage.getItem(FAVORIS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function addToFavoris(tableauId) {
  const favs = getFavoris();
  if (!favs.includes(tableauId)) {
    favs.push(tableauId);
    localStorage.setItem(FAVORIS_KEY, JSON.stringify(favs));
  }
  return true;
}

export function removeFromFavoris(tableauId) {
  const favs = getFavoris().filter((id) => id !== tableauId);
  localStorage.setItem(FAVORIS_KEY, JSON.stringify(favs));
  return true;
}

export function isFavori(tableauId) {
  return getFavoris().includes(tableauId);
}

// Fonctions de statistiques de visite - utiliser l'API
export async function incrementTableauViews(tableauId) {
  try {
    await axios.post(`${API_PREFIX}/views`, { tableauId });
  } catch (error) {
    console.error("Erreur lors de l'incrémentation des vues:", error.message);
    throw error;
  }
}

export async function getTableauStats(tableauId) {
  try {
    const res = await axios.get(`${API_PREFIX}/views/${tableauId}`);
    return res.data; // { views, uniqueVisitors }
  } catch (error) {
    console.error('Erreur lors de la récupération des stats:', error.message);
    throw error;
  }
}

// Initialisation du cache au démarrage
updateLocalCache().catch(console.debug);

// Synchronisation automatique toutes les 5 minutes (navigateur uniquement, pas SSR/tests)
if (typeof window !== 'undefined') {
  setInterval(() => syncPendingData().catch(console.debug), 5 * 60 * 1000);
}
